/**
 * @prettier
 */

import React, { Fragment, useEffect, useRef, useState, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import PhotoUploadIcon from 'assets/images/invitations/single-sample-modal/photo-upload-icon.svg';
import UploadcareWidget from 'components/common/UploadcareWidget/UploadcareWidget';
import { isUploadcareEnabled } from 'components/common/UploadcareWidget/constants';
import { autocropUploadcareFile } from 'components/common/UploadcareWidget/utils/crop';
import { ptToPx } from 'cards/util';
import { getMinImageSize } from 'cards/util/customization';
import { MAX_CARD_DIMENSION_INCHES } from 'cards/constants/Cards';
import { usePhotoObjectURL, usePhotoUploadHandler } from '../util/hooks';

import styles from './styles.module.less';

const UploadButton = ({ disabled, editable, invalid, onClick }) => {
  const buttonText =
    (disabled && 'Loading...') ||
    (editable && 'Edit Photo') ||
    (invalid && 'Try another file') ||
    'Upload a photo';

  return (
    <ButtonV2 disabled={disabled} onClick={onClick}>
      {buttonText}
    </ButtonV2>
  );
};

const FileUploader = ({ onSelect, renderButton }) => {
  const inputEl = useRef(null);

  const onClick = () => {
    inputEl.current.click();
  };

  const onSelectFile = () => {
    onSelect(inputEl.current.files[0]);
  };

  return (
    <Fragment>
      <input
        ref={inputEl}
        className={styles.hiddenInput}
        type="file"
        accept="image/jpeg,image/png"
        onChange={onSelectFile}
      />
      {renderButton(onClick)}
    </Fragment>
  );
};

const Dropzone = ({
  form,
  imageElement,
  updateForm,
  template,
  cardSuiteUUIDList,
  onSuccess,
  defaultTemplateSet,
}) => {
  const dropzoneIcon = <img alt="" src={PhotoUploadIcon} className={styles.dropzoneIcon} />;
  const busy = !(imageElement && imageElement.uuid);
  const {
    photo,
    uploadcareFile: previousUploadcareFile,
    variation,
    variationSet,
  } = form.customizations;
  const [minDimensions, setMinDimensions] = useState({ width: 0, height: 0 });
  const isMultiCardFlow = !!cardSuiteUUIDList; // When cardSuiteUUIDList is defined, we're in a multi-card flow. Voluntarily not using the isMulti prop.

  const onSelect = usePhotoUploadHandler(
    updateForm,
    minDimensions,
    false,
    null,
    cardSuiteUUIDList,
    defaultTemplateSet,
    onSuccess
  );

  // Single-card flow. Calculate the min size of the card's 1st customizable image.
  useEffect(() => {
    if (isMultiCardFlow || !imageElement || !template || !variation) return;

    const templatePage = template.pages[0];
    const maxCardDimensionInches = MAX_CARD_DIMENSION_INCHES[variation.option_values.size];
    const minSize = getMinImageSize(imageElement, templatePage, maxCardDimensionInches);

    setMinDimensions(minSize);
  }, [isMultiCardFlow, imageElement, template, variation]);

  // For multi-card flows, calculate the min width and height of the 1st image on
  // each card's 1st page. Use the largest min width/height requirement for the dropzone.
  useEffect(() => {
    if (!cardSuiteUUIDList || !defaultTemplateSet || !variationSet) return;

    const allMinWidths = [];
    const allMinHeights = [];

    cardSuiteUUIDList.forEach(uuid => {
      const firstPage = defaultTemplateSet[uuid].pages[0];
      const customizationSize = variationSet[uuid].option_values.size;
      const elements = (firstPage && firstPage.elements) || [];
      const firstCustomizableImage = elements.find(
        e => e.content_type === 'IMAGE' && e.customizable
      );

      if (firstCustomizableImage && firstPage && customizationSize) {
        const maxCardDimensionInches = MAX_CARD_DIMENSION_INCHES[customizationSize];
        const minSize = getMinImageSize(firstCustomizableImage, firstPage, maxCardDimensionInches);

        allMinWidths.push(minSize.width);
        allMinHeights.push(minSize.height);
      }
    });

    setMinDimensions({
      width: allMinWidths.length ? Math.max(...allMinWidths) : 0,
      height: allMinHeights.length ? Math.max(...allMinHeights) : 0,
    });
  }, [cardSuiteUUIDList, defaultTemplateSet, variationSet]);

  const saveUploadcareImageToForm = useCallback(
    newUploadcareImage => {
      if (!newUploadcareImage) return;

      let uploadcareFile = newUploadcareImage;
      const uploadcareFileSet = {};

      if (isMultiCardFlow) {
        // For the multi-card flow, crop the image seperately for each card's 1st customizable image slot.
        cardSuiteUUIDList.forEach(cardSuiteUUID => {
          const firstPage = defaultTemplateSet[cardSuiteUUID].pages[0];
          const customizableImageSlot =
            firstPage && firstPage.elements.find(e => e.content_type === 'IMAGE' && e.customizable);

          if (customizableImageSlot) {
            uploadcareFileSet[cardSuiteUUID] = autocropUploadcareFile(
              uploadcareFile,
              customizableImageSlot
            );
          }
        });
      } else if (imageElement) {
        // For the single-card flow, crop the image for the card's 1st customizable image slot
        uploadcareFile = autocropUploadcareFile(newUploadcareImage, imageElement);
      }

      updateForm({
        customizations: {
          uploadcareFile,
          uploadcareFileSet,
        },
      });

      onSuccess();
    },
    [defaultTemplateSet, updateForm, cardSuiteUUIDList, onSuccess, isMultiCardFlow, imageElement]
  );

  return (
    <div className={styles.dropzoneBox}>
      {dropzoneIcon}
      <div className={styles.dropzoneText}>
        Image must be at least {minDimensions.width} by {minDimensions.height} pixels and in JPG or
        PNG format.
        <br />
        (max 10MB)
      </div>
      <div>
        {isUploadcareEnabled ? (
          <UploadcareWidget
            cachedValue={previousUploadcareFile ? previousUploadcareFile.cdnUrl : null}
            minWidth={minDimensions.width}
            minHeight={minDimensions.height}
            onUploadcareFileReady={saveUploadcareImageToForm}
            analyticsProduct="CARDS"
            analyticsLocation="SINGLE_SAMPLES"
            renderButton={(inProgress, editable, onClick) => (
              <UploadButton
                disabled={inProgress}
                editable={editable}
                invalid={false}
                onClick={onClick}
              />
            )}
          />
        ) : (
          <FileUploader
            onSelect={onSelect}
            renderButton={onClick => (
              <UploadButton
                disabled={busy}
                editable={false}
                invalid={photo.file && !photo.valid}
                onClick={onClick}
              />
            )}
          />
        )}
      </div>
    </div>
  );
};

const Crop = ({ activePreview, form, page, updateForm, isMulti }) => {
  const { photo, photoSet } = form.customizations;

  const setPixelCrop = pixelCrop => {
    if (!isMulti) {
      updateForm({ customizations: { pixelCrop } });
    }

    if (activePreview) {
      updateForm({ customizations: { pixelCropSet: { [activePreview]: pixelCrop } } });
    }
  };

  const photoURL = usePhotoObjectURL(isMulti && activePreview ? photoSet[activePreview] : photo);

  const [crop, setCrop] = useState({ aspect: 1, width: 0, x: 0, y: 0 });

  useEffect(() => {
    const customImageElement =
      page && page.elements.find(e => e.content_type === 'IMAGE' && e.customizable);

    if (!customImageElement) {
      return;
    }

    const aspect = customImageElement.width / customImageElement.height;

    setCrop({ aspect, width: ptToPx(customImageElement.width), x: 0, y: 0 });
  }, [page]);

  const onChange = (inputCrop, inputPixelCrop) => {
    setCrop(inputCrop);
    setPixelCrop(inputPixelCrop);
  };

  if (!photoURL) {
    return null;
  }

  return (
    <ReactCrop
      className={styles.crop}
      src={photoURL}
      crop={crop}
      onComplete={onChange}
      onChange={onChange}
      keepSelection
    />
  );
};

export default { UploadButton, FileUploader, Dropzone, Crop };
